import { useEffect, useState } from 'react';
import type { FollowUpDto } from '@ecom/contracts';
import { AsyncSelect } from '@/components/async-select';
import { Sheet } from '@/components/page';
import { Alert, Button, ConfirmDialog, Field, Input, Select, Textarea } from '@/components/ui';
import { useT } from '@/i18n/use-t';
import { customerOptions, followUpAssigneeOptions } from '@/lib/options';
import { useAuthStore } from '@/stores/auth.store';
import { useFollowUpsStore } from '@/stores/follow-ups.store';

/** `YYYY-MM-DDTHH:mm` in the reader's own clock — what a datetime field shows. */
export function localInput(value: Date): string {
  const pad = (number: number) => String(number).padStart(2, '0');
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}T${pad(value.getHours())}:${pad(value.getMinutes())}`;
}

/** Tomorrow at ten: the time most call-backs are promised for. */
function tomorrowAtTen(): string {
  const day = new Date();
  day.setDate(day.getDate() + 1);
  day.setHours(10, 0, 0, 0);
  return localInput(day);
}

/**
 * Adding or changing a follow-up (ADR-104): what, when, who, and about whom.
 *
 * From a customer's page the customer is fixed and their orders are offered;
 * from the follow-ups page any customer can be picked. It is given to the
 * person adding it unless they choose someone else, or nobody yet.
 */
export function FollowUpSheet({
  followUp,
  customer,
  orders,
  onClose,
}: {
  followUp: FollowUpDto | null;
  /** Fixed when added from their page. */
  customer?: { id: string; name: string };
  /** Their orders to choose from, newest first. */
  orders?: { id: string; number: string }[];
  onClose: () => void;
}) {
  const t = useT();
  const me = useAuthStore((state) => state.user);
  const { save, remove, saving, saveError, clearSaveError } = useFollowUpsStore();
  const [title, setTitle] = useState(followUp?.title ?? '');
  const [note, setNote] = useState(followUp?.note ?? '');
  const [due, setDue] = useState(followUp ? localInput(new Date(followUp.dueAt)) : tomorrowAtTen());
  const [assigneeId, setAssigneeId] = useState(followUp ? (followUp.assignee?.id ?? '') : (me?.id ?? ''));
  const [customerId, setCustomerId] = useState(customer?.id ?? followUp?.customer?.id ?? '');
  const [orderId, setOrderId] = useState(followUp?.order?.id ?? '');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    clearSaveError();
    if (followUp?.assignee) followUpAssigneeOptions.remember(followUp.assignee.id, followUp.assignee.name);
    if (me && !followUp) followUpAssigneeOptions.remember(me.id, me.name);
  }, [clearSaveError, followUp, me]);

  const dueAt = due ? new Date(due) : null;
  const ready = title.trim().length > 0 && dueAt !== null && !Number.isNaN(dueAt.getTime()) && Boolean(customerId || orderId);
  const choices = orders ?? (followUp?.order ? [followUp.order] : []);

  async function submit() {
    if (!ready || !dueAt) return;
    const body = {
      title: title.trim(),
      note: note.trim() || null,
      dueAt: dueAt.toISOString(),
      assigneeId: assigneeId || null,
      customerId: customerId || null,
      orderId: orderId || null,
    };
    if (await save(followUp?.id ?? null, body)) onClose();
  }

  // The API decides; this only spares a button that would be refused.
  const mayDelete = Boolean(followUp) && (me?.role === 'OWNER' || followUp?.createdBy === me?.name);

  return (
    <>
      <Sheet
        open
        title={followUp ? t('followUpEdit') : t('followUpAdd')}
        subtitle={customer?.name ?? followUp?.customer?.name ?? undefined}
        onClose={() => {
          if (!deleting) onClose();
        }}
        footer={
          <>
            {mayDelete ? (
              <Button variant="secondary" className="me-auto text-danger" onClick={() => setDeleting(true)} disabled={saving}>
                {t('delete')}
              </Button>
            ) : null}
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              {t('cancel')}
            </Button>
            <Button disabled={!ready || saving} onClick={() => void submit()}>
              {t('save')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <Field label={t('followUpTitle')} htmlFor="follow-up-title">
            <Input
              id="follow-up-title"
              maxLength={200}
              placeholder={t('followUpTitlePlaceholder')}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('followUpDue')} htmlFor="follow-up-due">
              <Input id="follow-up-due" type="datetime-local" dir="ltr" value={due} onChange={(event) => setDue(event.target.value)} />
            </Field>
            <Field label={t('followUpAssignee')} htmlFor="follow-up-assignee">
              <AsyncSelect
                id="follow-up-assignee"
                value={assigneeId}
                emptyLabel={t('followUpNobody')}
                fetchPage={(search, cursor) => followUpAssigneeOptions.page(search, cursor)}
                fetchOne={(id) => followUpAssigneeOptions.one(id)}
                onChange={(id) => setAssigneeId(id)}
              />
            </Field>
          </div>
          {customer ? null : (
            <Field label={t('customer')} htmlFor="follow-up-customer">
              <AsyncSelect
                id="follow-up-customer"
                value={customerId}
                emptyLabel={t('anyValue')}
                fetchPage={(search, cursor) => customerOptions.page(search, cursor)}
                fetchOne={(id) => customerOptions.one(id)}
                onChange={(id) => {
                  // An order belongs to its customer: another customer, no order.
                  if (id !== customerId) setOrderId('');
                  setCustomerId(id);
                }}
              />
            </Field>
          )}
          {choices.length > 0 ? (
            <Field label={t('followUpOrder')} htmlFor="follow-up-order" hint={t('optional')}>
              <Select id="follow-up-order" value={orderId} onChange={(event) => setOrderId(event.target.value)}>
                <option value="">{t('followUpOrderNone')}</option>
                {choices.map((order) => (
                  <option key={order.id} value={order.id}>
                    {order.number}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          <Field label={t('followUpNote')} htmlFor="follow-up-note" hint={t('optional')}>
            <Textarea id="follow-up-note" rows={4} maxLength={2000} value={note} onChange={(event) => setNote(event.target.value)} />
          </Field>
          {saveError ? <Alert>{saveError}</Alert> : null}
        </div>
      </Sheet>
      <ConfirmDialog
        open={deleting}
        title={t('followUpDeleteTitle')}
        body={t('followUpDeleteBody')}
        confirmLabel={t('delete')}
        cancelLabel={t('cancel')}
        busy={saving}
        onConfirm={async () => {
          if (followUp && (await remove(followUp.id))) onClose();
          setDeleting(false);
        }}
        onCancel={() => setDeleting(false)}
      />
    </>
  );
}
